/**
 * Wire schema for `chat-ws`'s server-to-client events.
 *
 * Transcribed from the gateway's own event contract
 * (`ezspot-api/src/messaging/chat-events.ts`, `chat.gateway.ts`), not
 * invented. Parsed with zod rather than cast, and leniently — unknown keys
 * strip by default, so a server one release ahead of this build doesn't break
 * it. Parsing must never throw: a malformed payload costs one un-rendered
 * event the next REST fetch already covers, while an exception inside a
 * socket handler would take the whole thread down.
 */
import { z } from "zod";
import { SUPPORT_DISPLAY_NAME } from "~/app/(site)/messages/_lib/constants";
import type {
  ConversationDetail,
  MessageAttachment,
  MessageItem,
} from "~/server/services/messaging.types";

const messageAttachmentSchema = z.object({
  id: z.string(),
  url: z.string().nullable(),
  mime_type: z.string(),
  file_size_bytes: z.number(),
});

export const messageCreatedEventSchema = z.object({
  conversation_id: z.string().uuid(),
  message_id: z.string().uuid(),
  sender_user_id: z.string().uuid().nullable(),
  is_system: z.boolean(),
  body: z.string().nullable(),
  attachments: z.array(messageAttachmentSchema),
  sent_at: z.string(),
  /**
   * OPTIONAL, not merely nullable: a server older than the release that added
   * this field omits the key entirely, and rejecting those events would be far
   * worse than falling back to `isPendingEcho`'s content match. Null when the
   * server does send it for a row nobody's device keyed — staff and system.
   */
  idempotency_key: z.string().uuid().nullable().optional(),
});
export type MessageCreatedEvent = z.infer<typeof messageCreatedEventSchema>;

export const typingEventSchema = z.object({
  conversation_id: z.string().uuid(),
  user_id: z.string().uuid(),
});
export type TypingEvent = z.infer<typeof typingEventSchema>;

/** The ack payload for a `subscribe` emit — the one event on this socket that
 *  arrives via a callback rather than an `on` listener, so it's just as
 *  server-controlled as the rest and needs the same parse-don't-cast
 *  treatment. */
export const subscribeAckSchema = z.union([
  z.object({ ok: z.literal(true) }),
  z.object({ ok: z.literal(false), error: z.string() }),
]);
export type SubscribeAck = z.infer<typeof subscribeAckSchema>;

/** Never throws. `null` means "drop this event," not "crash the socket." */
export function parseMessageCreatedEvent(
  raw: unknown,
): MessageCreatedEvent | null {
  const result = messageCreatedEventSchema.safeParse(raw);
  return result.success ? result.data : null;
}

/** Never throws — shared by `typing.started` and `typing.stopped`, same shape. */
export function parseTypingEvent(raw: unknown): TypingEvent | null {
  const result = typingEventSchema.safeParse(raw);
  return result.success ? result.data : null;
}

/** Never throws. `null` means "treat like a settled non-retryable ack" —
 *  same as `not_found` / `invalid_payload`, since an ack we can't make sense
 *  of isn't one a retry would fix either. */
export function parseSubscribeAck(raw: unknown): SubscribeAck | null {
  const result = subscribeAckSchema.safeParse(raw);
  return result.success ? result.data : null;
}

export interface NormalizedMessageCreated {
  message: MessageItem;
  /**
   * True when the sender is a real participant the conversation snapshot
   * doesn't know about yet (support joining a thread fetched before they
   * did). The message still renders, unattributed — the caller should
   * refetch the conversation so the *next* one is named.
   */
  sawUnknownSender: boolean;
}

/**
 * Maps a `message.created` socket payload to the `MessageItem` shape the REST
 * history endpoint returns.
 *
 * A socket payload is a transport event, not a history row: the id key is
 * `message_id`, `idempotency_key` may be absent rather than null (see the
 * schema) and is normalised to null here so the row has the history shape,
 * and there is no `sender.display_name` on the wire at all.
 * `display_name` is resolved from the conversation's own participant list
 * instead, in three cases that are not interchangeable:
 *
 * - system note: centred and unattributed in `MessageBubble` — no name to
 *   look up and none to print.
 * - staff reply (`sender_user_id` null, `is_system` false): always
 *   `SUPPORT_DISPLAY_NAME` — chat-ws has no business naming which person at
 *   EZSpot typed it.
 * - participant: matched by id.
 */
export function toMessageItem(
  event: MessageCreatedEvent,
  conversation: Pick<ConversationDetail, "participants"> | undefined,
): NormalizedMessageCreated {
  const attachments: MessageAttachment[] = event.attachments;
  const base = {
    id: event.message_id,
    conversation_id: event.conversation_id,
    body: event.body,
    attachments,
    redacted: false,
    sent_at: event.sent_at,
    idempotency_key: event.idempotency_key ?? null,
  };

  if (event.is_system) {
    return {
      message: {
        ...base,
        sender: {
          user_id: null,
          display_name: "",
          role: null,
          is_system: true,
        },
      },
      sawUnknownSender: false,
    };
  }

  if (event.sender_user_id === null) {
    return {
      message: {
        ...base,
        sender: {
          user_id: null,
          display_name: SUPPORT_DISPLAY_NAME,
          role: "support",
          is_system: false,
        },
      },
      sawUnknownSender: false,
    };
  }

  const participant = conversation?.participants.find(
    (row) => row.user_id === event.sender_user_id,
  );

  return {
    message: {
      ...base,
      sender: {
        user_id: event.sender_user_id,
        display_name: participant?.display_name ?? "",
        role: participant?.role ?? null,
        is_system: false,
      },
    },
    sawUnknownSender: !participant,
  };
}
